// Attendance — one employee's month.
//
// Summary tiles, a colour-coded calendar (Sun → Sat) with in/out per day, and
// a day-wise table listing every punch. Exports to Excel.
import { useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Box, Stack, Typography, TextField, Button, Tooltip, IconButton } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import HowToRegOutlinedIcon from "@mui/icons-material/HowToRegOutlined";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import PersonOffOutlinedIcon from "@mui/icons-material/PersonOffOutlined";
import WbTwilightOutlinedIcon from "@mui/icons-material/WbTwilightOutlined";
import DashboardLayout from "../../layouts/DashboardLayout";
import FlatCard from "../../components/FlatCard";
import { useToast } from "../../components/ToastProvider";
import { useEmployeeAttendance } from "../../hooks/queries";
import { exportToExcel } from "../../utils/excelExport";
import { COLORS } from "../../theme/tokens";
import { HEADING_FONT } from "../../theme/fonts";
import {
  PageHeader,
  AttendanceError,
  KpiCard,
  SectionTitle,
  DataTable,
  StatusChip,
  StatusLegend,
  ExportButton,
  PunchSummary,
  firstPunchOf,
  lastPunchOf,
  middlePunches,
  InOutCount,
  timelineText,
  statusMeta,
  fmtTime,
  fmtMinutes,
  fmtDate,
  fmtMonth,
  ym,
  ymd,
  parseYmd,
  secondaryBtnSx,
  iconBtnSx,
  controlFieldSx,
} from "./attendanceUi";

// Calendar colours: light tint background, a solid accent strip + badge.
const CAL = {
  present:      { solid: "#10B981", bg: "#F0FDF9", border: "#A7F3D0" },
  half_day:     { solid: "#F59E0B", bg: "#FFFBEB", border: "#FDE68A" },
  absent:       { solid: "#F43F5E", bg: "#FFF1F3", border: "#FECDD3" },
  missed_punch: { solid: "#8B5CF6", bg: "#F5F3FF", border: "#DDD6FE" },
  in_office:    { solid: "#0EA5E9", bg: "#F0F9FF", border: "#BAE6FD" },
  outside:      { solid: "#F97316", bg: "#FFF7ED", border: "#FED7AA" },
  not_in:       { solid: "#94A3B8", bg: "#F8FAFC", border: "#E2E8F0" },
  weekly_off:   { solid: "#64748B", bg: "#F1F5F9", border: "#CBD5E1" },
  holiday:      { solid: "#14B8A6", bg: "#F0FDFA", border: "#99F6E4" },
  today: "#6366F1",
  late: "#D97706",
  sunday: "#F43F5E",
};

const WEEK = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const shiftMonth = (month, delta) => {
  const d = parseYmd(`${month}-01`);
  d.setMonth(d.getMonth() + delta);
  return ym(d);
};

function EmployeeAttendance() {
  const { code } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const [params, setParams] = useSearchParams();
  const thisMonth = ym(new Date());
  const month = params.get("month") || thisMonth;
  const setMonth = (m) => setParams({ month: m }, { replace: true });
  const [exporting, setExporting] = useState(false);
  const today = ymd(new Date());

  const { data, isLoading, error, refetch, isFetching } = useEmployeeAttendance(code, month);
  const emp = data?.employee;
  const t = data?.totals || {};
  const days = useMemo(() => data?.days || [], [data]);

  const leadingBlanks = days.length ? parseYmd(days[0].date).getDay() : 0;
  const pastDays = useMemo(() => days.filter((d) => d.date <= today).slice().reverse(), [days, today]);

  async function handleExport() {
    setExporting(true);
    try {
      await exportToExcel({
        fileName: `Attendance-${emp.employeeCode}-${month}.xlsx`,
        sheets: [
          {
            name: `${emp.employeeCode}-${month}`,
            title: `${emp.name || `Code ${emp.employeeCode}`} (#${emp.employeeCode}) — ${fmtMonth(month)}`,
            subtitle: `Present ${t.present} · Half Day ${t.halfDay} · Absent ${t.absent} · Late ${t.late} · Payable ${t.payableDays} days`,
            columns: [
              { header: "Date", key: "date", width: 16 },
              { header: "Day", key: "day", width: 7, align: "center" },
              { header: "First Punch", key: "in", width: 12, align: "center" },
              { header: "Last Punch", key: "out", width: 12, align: "center" },
              { header: "IN Count", key: "inCount", width: 9, align: "center" },
              { header: "OUT Count", key: "outCount", width: 10, align: "center" },
              { header: "Inside Time", key: "work", width: 12, align: "center" },
              { header: "Outside Time", key: "outside", width: 12, align: "center" },
              { header: "Late By", key: "late", width: 10, align: "center" },
              { header: "Early By", key: "early", width: 10, align: "center" },
              { header: "Status", key: "status", width: 15, align: "center" },
              { header: "Full Day Timeline", key: "punches", width: 60 },
            ],
            rows: days
              .filter((d) => d.date <= today)
              .map((d) => ({
                date: fmtDate(d.date, { day: "2-digit", month: "short", year: "numeric" }),
                day: WEEK[parseYmd(d.date).getDay()],
                in: firstPunchOf(d) ? fmtTime(firstPunchOf(d)) : "",
                out: lastPunchOf(d) ? fmtTime(lastPunchOf(d)) : "",
                work: d.workMinutes ? fmtMinutes(d.workMinutes) : "",
                outside: d.outsideMinutes ? fmtMinutes(d.outsideMinutes) : "",
                inCount: d.inCount ?? "",
                outCount: d.outCount ?? "",
                late: d.isLate ? fmtMinutes(d.lateMinutes) : "",
                early: d.isEarly ? fmtMinutes(d.earlyMinutes) : "",
                status: statusMeta(d.status).label + (d.holidayName ? ` (${d.holidayName})` : ""),
                punches: timelineText(d.punches || []),
                _status: d.status,
              })),
            cellFill: (row, col) => (col?.key === "status" ? statusMeta(row._status).excel : null),
          },
        ],
      });
    } catch (err) {
      toast.error(err.message || "Export failed");
    } finally {
      setExporting(false);
    }
  }

  const columns = [
    { key: "date", label: "Date", width: "minmax(140px, 1.2fr)", align: "left", render: (d) => <Typography sx={{ fontSize: 13.5, fontWeight: 600 }}>{fmtDate(d.date, { weekday: "short", day: "2-digit", month: "short" })}</Typography> },
    { key: "in", label: "First Punch", width: "minmax(95px, 1fr)", align: "center", render: (d) => <Typography sx={{ fontSize: 13.5, fontWeight: 600, color: d.isLate ? COLORS.warning : COLORS.ink }}>{fmtTime(firstPunchOf(d))}</Typography> },
    { key: "last", label: "Last Punch", width: "minmax(95px, 1fr)", align: "center", render: (d) => <Typography sx={{ fontSize: 13.5, fontWeight: 600, color: COLORS.ink }}>{fmtTime(lastPunchOf(d))}</Typography> },
    { key: "timeline", label: "IN / OUT (click)", width: "minmax(175px, 1fr)", align: "center", render: (d) => <PunchSummary punches={middlePunches(d.punches)} /> },
    { key: "count", label: "Count", width: "minmax(105px, 1fr)", align: "center", render: (d) => <InOutCount inCount={d.inCount} outCount={d.outCount} /> },
    { key: "work", label: "Inside", width: "minmax(75px, 1fr)", align: "center", render: (d) => fmtMinutes(d.workMinutes) },
    { key: "outside", label: "Outside", width: "minmax(75px, 1fr)", align: "center", render: (d) => fmtMinutes(d.outsideMinutes) },
    { key: "status", label: "Status", width: "minmax(130px, 1fr)", align: "center", render: (d) => <StatusChip status={d.status} late={d.isLate} early={d.isEarly} /> },
  ];

  return (
    <DashboardLayout>
      <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(-1)} sx={{ ...secondaryBtnSx, mb: 2, py: 0.5 }}>
        Back
      </Button>

      <PageHeader
        title={emp ? emp.name || `Code ${emp.employeeCode}` : error ? "Employee not found" : "Loading…"}
        subtitle={emp ? `#${emp.employeeCode}${emp.department ? ` · ${emp.department}` : ""} · ${fmtMonth(month)}` : error?.message}
        actions={
          <>
            <Stack direction="row" spacing={0.75} sx={{ alignItems: "center" }}>
              <Tooltip title="Previous month">
                <IconButton size="small" onClick={() => setMonth(shiftMonth(month, -1))} sx={iconBtnSx}>
                  <ChevronLeftIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <TextField type="month" size="small" value={month} onChange={(e) => e.target.value && setMonth(e.target.value)} inputProps={{ max: thisMonth }} sx={{ ...controlFieldSx, width: 165 }} />
              <Tooltip title="Next month">
                <span style={{ display: "inline-flex" }}>
                  <IconButton size="small" disabled={month >= thisMonth} onClick={() => setMonth(shiftMonth(month, 1))} sx={iconBtnSx}>
                    <ChevronRightIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Box sx={{ width: 4 }} />
              <ExportButton onClick={handleExport} busy={exporting || !emp} />
            </Stack>
          </>
        }
      />
      <AttendanceError error={error} onRetry={() => refetch()} retrying={isFetching} />

      {/* Summary (2 x 2 on the left) + calendar (right), one row */}
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "minmax(300px, 1fr) auto" }, gap: 2, mb: 2.5, alignItems: "stretch" }}>
      <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gridAutoRows: "1fr", gap: { xs: 1.5, md: 2 } }}>
        <KpiCard label="Present" value={t.present ?? "—"} accent={COLORS.success} icon={<HowToRegOutlinedIcon />} center />
        <KpiCard label="Half Day" value={t.halfDay ?? "—"} accent="#B45309" icon={<WbTwilightOutlinedIcon />} center />
        <KpiCard label="Absent" value={t.absent ?? "—"} accent={COLORS.danger} icon={<PersonOffOutlinedIcon />} sub={t.missedPunch ? `+ ${t.missedPunch} missed punch` : null} center />
        <KpiCard label="Late Days" value={t.late ?? "—"} accent={COLORS.warning} icon={<AccessTimeOutlinedIcon />} center />
      </Box>

      {/* Calendar — compact; a day grows slightly on hover */}
      <FlatCard sx={{ minWidth: 0 }}>
        <SectionTitle>Calendar</SectionTitle>
        <Box sx={{ p: 2, overflowX: "auto" }}>
          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(7, 72px)", gap: 0.75 }}>
            {WEEK.map((w, i) => (
              <Typography key={w} sx={{ fontFamily: HEADING_FONT, fontSize: 10.5, fontWeight: 700, textTransform: "uppercase", color: i === 0 ? CAL.sunday : COLORS.muted, textAlign: "center", pb: 0.25 }}>
                {w}
              </Typography>
            ))}
            {Array.from({ length: leadingBlanks }).map((_, i) => (
              <Box key={`blank-${i}`} />
            ))}
            {(isLoading ? [] : days).map((d) => {
              const c = CAL[d.status] || null;
              const isToday = d.date === today;
              const m = statusMeta(d.status);
              const tip = d.status
                ? `${fmtDate(d.date)} · ${m.label}${d.isLate ? " · Late" : ""}`
                : fmtDate(d.date);
              return (
                <Tooltip key={d.date} title={tip} arrow>
                  <Box
                    sx={{
                      position: "relative",
                      aspectRatio: "1 / 1",
                      px: 0.6,
                      py: 0.5,
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "space-between",
                      borderRadius: "10px",
                      background: c ? c.bg : "#FAFBFC",
                      border: `1px solid ${isToday ? CAL.today : c ? c.border : COLORS.hairline}`,
                      boxShadow: isToday ? `0 0 0 2px ${CAL.today}33` : "none",
                      borderTop: c ? `3px solid ${c.solid}` : undefined,
                      opacity: d.status ? 1 : 0.5,
                      cursor: "default",
                      transition: "transform 0.15s ease, box-shadow 0.15s ease",
                      "&:hover": { transform: "scale(1.1)", zIndex: 2, boxShadow: "0 8px 22px rgba(15,23,42,0.16)", opacity: 1 },
                    }}
                  >
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <Typography sx={{ fontSize: 13, fontWeight: 800, color: isToday ? CAL.today : COLORS.ink }}>{parseYmd(d.date).getDate()}</Typography>
                      {d.isLate && <Box sx={{ width: 6, height: 6, flexShrink: 0, borderRadius: "50%", background: CAL.late }} />}
                    </Box>
                    {c && (
                      <Typography noWrap sx={{ fontSize: 9.5, fontWeight: 700, color: c.solid, lineHeight: 1.2 }}>
                        {d.status === "missed_punch" ? "Missed" : m.label}
                      </Typography>
                    )}
                  </Box>
                </Tooltip>
              );
            })}
          </Box>
        </Box>
        <Box sx={{ px: 2, pb: 1.75, pt: 0.25, maxWidth: 7 * 72 + 6 * 6 + 32 }}>
          <StatusLegend keys={["present", "in_office", "half_day", "absent", "missed_punch", "weekly_off", "holiday"]} colors={CAL} lateColor={CAL.late} />
        </Box>
      </FlatCard>
      </Box>

      {/* Day-wise detail */}
      <FlatCard>
        <SectionTitle>Day-wise Punches</SectionTitle>
        <DataTable
          columns={columns}
          rows={pastDays}
          loading={isLoading}
          rowKey={(d) => d.date}
          emptyText="No days to show"
        />
      </FlatCard>
    </DashboardLayout>
  );
}

export default EmployeeAttendance;
